// src/app/services/price.service.ts
import { catchError, switchMap } from 'rxjs/operators';
import { of} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PriceService {
  /** Associe tes symboles aux IDs CoinGecko */
  private readonly CG_ID: Record<string, string> = {
    ETH: 'ethereum',
    WETH: 'weth',
    USDC: 'usd-coin',
    USDT: 'tether',
    DAI: 'dai',
    MATIC: 'matic-network',
    WBTC: 'wrapped-bitcoin',
     RETH: 'rocket-pool-eth',   // ← add
  STETH: 'staked-ether'
  };

  constructor(private http: HttpClient) {}

  /**
   * Retourne { ETH: 3290.12, USDC: 0.93, … } dans la devise demandée
   */
  fetchPrices(symbols: string[], vs = 'eur'): Observable<Record<string, number>> {
    const ids = symbols
      .map(s => this.CG_ID[s])
      .filter(Boolean)
      .join(',');
    const url = `https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=${vs}`;

    return this.http.get<any>(url).pipe(
      map(resp => {
        const out: Record<string, number> = {};
        for (const sym of symbols) {
          const id = this.CG_ID[sym];
          out[sym] = resp[id]?.[vs] ?? 0;
        }
        return out;
      })
    );
  }


  // price.service.ts
fetchPricesByTokens(
  tokens: { symbol: string; address: string; chainId: number }[],
  vs = 'usd' // ← Garde USD, la conversion fiat est faite dans le composant
): Observable<Record<string, number>> {

  const symbols = Array.from(new Set(tokens.map(t => t.symbol.toUpperCase())));
  const ids = symbols
    .map(sym => this.CG_ID[sym])
    .filter(Boolean)
    .join(',');

  const url = `https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=${vs}`;

  return this.http.get<any>(url).pipe(
    map(resp => {
      const out: Record<string, number> = {};
      for (const t of tokens) {
        const sym = t.symbol.toUpperCase();
        const id = this.CG_ID[sym];
        const price = id ? (resp[id]?.[vs] ?? 0) : 0;

        // 🔑 clé attendue partout dans ton composant
        const key = `${(t.address || '').toLowerCase()}_${t.chainId}`;
        out[key] = price;
      }
      return out;
    })
  );
}

  fetchFiatRates(forceRefresh = false): Observable<Record<string, number>> {
    const cacheKey = 'fiatRatesCache_v2';
    const cacheTTL = 12 * 60 * 60 * 1000; // 12h

    const required = [
      'USD', 'EUR', 'GBP', 'CAD',
      'INR', 'IDR', 'VND', 'TRY', 'NGN',
      'BRL', 'KHR', 'ZAR', 'THB', 'MYR',
      'PHP'   // 🇵🇭 ajout nouveau
    ];

    if (!forceRefresh) {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        const fresh = Date.now() - parsed.timestamp < cacheTTL;
        const hasAll = required.every(k => parsed.data?.[k] !== undefined);
        if (fresh && hasAll) {
          return of(parsed.data);
        }
      }
    }

    // --- 1) Tentative principale : /exchange_rates ---
    const url = 'https://api.coingecko.com/api/v3/exchange_rates';
    return this.http.get<any>(url).pipe(
      map(data => {
        const r = data?.rates || {};
        const usd = r?.usd?.value || 1;

        if (
          r?.eur && r?.gbp && r?.cad && r?.inr && r?.idr && r?.vnd &&
          r?.try && r?.ngn && r?.brl && r?.khr && r?.zar && r?.thb &&
          r?.myr && r?.php   // 🇵🇭 check PHP ajouté ici
        ) {
          const rates: Record<string, number> = {
            USD: 1,
            EUR: r.eur.value / usd,
            GBP: r.gbp.value / usd,
            CAD: r.cad.value / usd,
            INR: r.inr.value / usd,
            IDR: r.idr.value / usd,
            VND: r.vnd.value / usd,
            TRY: r.try.value / usd,
            NGN: r.ngn.value / usd,
            BRL: r.brl.value / usd,
            KHR: r.khr.value / usd,
            ZAR: r.zar.value / usd,
            THB: r.thb.value / usd,
            MYR: r.myr.value / usd,
            PHP: r.php.value / usd   // 🇵🇭 mapping principal ajouté ici
          };
          localStorage.setItem(cacheKey, JSON.stringify({
            timestamp: Date.now(),
            data: rates
          }));
          return rates;
        }

        throw new Error('missing_rates');
      }),

      // --- 2) Fallback simple/price ---
      catchError((err: unknown) => {
        console.warn('Fallback simple/price triggered:', err);

        const vs = [
          'usd', 'eur', 'gbp', 'cad',
          'inr', 'idr', 'vnd', 'try', 'ngn',
          'brl', 'khr', 'zar', 'thb', 'myr',
          'php'   // 🇵🇭 fallback ajouté ici
        ].join(',');

        const u = `https://api.coingecko.com/api/v3/simple/price?ids=usd-coin&vs_currencies=${vs}`;

        return this.http.get<any>(u).pipe(
          map(resp => {
            const usdc = resp?.['usd-coin'] || {};
            const rates: Record<string, number> = {
              USD: 1,
              EUR: usdc.eur ?? 0,
              GBP: usdc.gbp ?? 0,
              CAD: usdc.cad ?? 0,
              INR: usdc.inr ?? 0,
              IDR: usdc.idr ?? 0,
              VND: usdc.vnd ?? 0,
              TRY: usdc.try ?? 0,
              NGN: usdc.ngn ?? 0,
              BRL: usdc.brl ?? 0,
              KHR: usdc.khr ?? 0,
              ZAR: usdc.zar ?? 0,
              THB: usdc.thb ?? 0,
              MYR: usdc.myr ?? 0,
              PHP: usdc.php ?? 0   // 🇵🇭 fallback mapping
            };

            localStorage.setItem(cacheKey, JSON.stringify({
              timestamp: Date.now(),
              data: rates
            }));
            return rates;
          })
        );
      })
    );
  }






}
